if (!customElements.get('td-tabbed-grouping')) {
  /**
   * Accessible tabbed grouping component for featured collections.
   *
   * Enhancements over the previous implementation:
   * - Uses AbortController for listener cleanup
   * - Uses for..of loops and early returns per standards
   * - Adds ARIA roles and keyboard interactions (Left/Right, Home/End, Enter/Space)
   * - Avoids DOM queries in the constructor
   */
  class TdTabbedGrouping extends HTMLElement {
    /** @type {AbortController} */
    #controller = new AbortController();

    /**
     * All clickable tab elements.
     * @returns {NodeListOf<HTMLElement>}
     */
    get tabSelectors() {
      return this.querySelectorAll('[data-tab-selector]');
    }

    /**
     * All tab panel/content elements.
     * @returns {NodeListOf<HTMLElement>}
     */
    get tabPanels() {
      return this.querySelectorAll('[data-tab-content]');
    }

    /**
     * Optional "View All" link element.
     * @returns {HTMLAnchorElement | null}
     */
    get viewAllLink() {
      const el = this.querySelector('[data-view-all]');
      return el instanceof HTMLAnchorElement ? el : null;
    }

    /**
     * Tablist container if present.
     * @returns {HTMLElement | null}
     */
    get tabListContainer() {
      const firstTab = this.querySelector('[data-tab-selector]');
      return firstTab?.parentElement ?? null;
    }

    connectedCallback() {
      const { signal } = this.#controller;

      // Ensure we have the required elements
      if (!this.tabSelectors.length || !this.tabPanels.length) return;

      this.#initializeAria();
      this.#syncInitialState();

      for (const tab of this.tabSelectors) {
        tab.addEventListener('click', this.#handleClick, { signal });
        tab.addEventListener('keydown', this.#handleKeydown, { signal });

        // Ensure keyboard focusability for non-native controls
        if (!tab.hasAttribute('tabindex')) tab.setAttribute('tabindex', '0');
      }
    }

    disconnectedCallback() {
      // Abort all event listeners registered with this signal
      this.#controller.abort();
    }

    /**
     * Initialize ARIA roles and relationships for tabs and panels.
     */
    #initializeAria() {
      // Tablist container semantics
      const tablist = this.tabListContainer;
      if (tablist) {
        if (!tablist.hasAttribute('role')) tablist.setAttribute('role', 'tablist');
        if (!tablist.hasAttribute('aria-label') && !tablist.hasAttribute('aria-labelledby')) {
          tablist.setAttribute('aria-label', 'Collections');
        }
      }

      // Associate tabs and panels
      for (const tab of this.tabSelectors) {
        const itemId = tab.dataset.itemId ?? '';
        if (!itemId) continue;

        const tabId = `tab-${itemId}`;
        tab.setAttribute('role', 'tab');
        tab.id ||= tabId;
      }

      for (const panel of this.tabPanels) {
        const itemId = panel.dataset.itemId ?? '';
        if (!itemId) continue;

        const panelId = `panel-${itemId}`;
        const tabId = `tab-${itemId}`;
        panel.setAttribute('role', 'tabpanel');
        panel.id ||= panelId;
        panel.setAttribute('aria-labelledby', tabId);
      }
    }

    /**
     * Ensure ARIA selected state, roving tabindex, and hidden states reflect initial data attributes.
     */
    #syncInitialState() {
      // Determine which tab is active
      let activeTab = null;
      for (const tab of this.tabSelectors) {
        if (tab.dataset.tabActive === 'true') {
          activeTab = tab;
          break;
        }
      }

      // Fallback to the first tab if none is marked active
      if (!activeTab) activeTab = this.tabSelectors[0] ?? null;
      if (!activeTab) return;

      this.#activateByItemId(activeTab.dataset.itemId ?? '', { focus: false });
    }

    /**
     * Click handler for tabs.
     * @param {MouseEvent} event
     */
    #handleClick = (event) => {
      const target = event.currentTarget;
      if (!(target instanceof HTMLElement)) return;

      const itemId = target.dataset.itemId ?? '';
      if (!itemId) return;

      this.#activateByItemId(itemId, { focus: true });
    };

    /**
     * Keyboard interaction for tabs.
     * Supports Left/Right arrows, Home/End, and Enter/Space activation.
     * @param {KeyboardEvent} event
     */
    #handleKeydown = (event) => {
      const current = event.currentTarget;
      if (!(current instanceof HTMLElement)) return;

      const tabs = Array.from(this.tabSelectors);
      const currentIndex = tabs.indexOf(current);
      if (currentIndex === -1) return;

      const isHorizontal = true; // Only horizontal tabs used here

      switch (event.key) {
        case 'ArrowLeft':
          if (!isHorizontal) return;
          event.preventDefault();
          this.#focusTabByIndex((currentIndex - 1 + tabs.length) % tabs.length);
          break;
        case 'ArrowRight':
          if (!isHorizontal) return;
          event.preventDefault();
          this.#focusTabByIndex((currentIndex + 1) % tabs.length);
          break;
        case 'Home':
          event.preventDefault();
          this.#focusTabByIndex(0);
          break;
        case 'End':
          event.preventDefault();
          this.#focusTabByIndex(tabs.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.#activateByItemId(current.dataset.itemId ?? '', { focus: true });
          break;
        default:
          break;
      }
    };

    /**
     * Focuses a tab by index without activating it.
     * @param {number} index
     */
    #focusTabByIndex(index) {
      const tabs = Array.from(this.tabSelectors);
      const target = tabs[index];
      if (target instanceof HTMLElement) target.focus();
    }

    /**
     * Activates tab and panel by item id.
     * Updates aria-selected, roving tabindex, data attributes and hidden state.
     * @param {string} itemId
     * @param {{ focus: boolean }} options
     */
    #activateByItemId(itemId, { focus }) {
      if (!itemId) return;

      let activeTab = null;

      // Update tabs
      for (const tab of this.tabSelectors) {
        const isActive = tab.dataset.itemId === itemId;
        tab.setAttribute('data-tab-active', isActive ? 'true' : 'false');
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');

        if (isActive) activeTab = tab;
      }

      // Update panels
      for (const panel of this.tabPanels) {
        const isActive = panel.dataset.itemId === itemId;
        panel.setAttribute('data-tab-active', isActive ? 'true' : 'false');
        panel.toggleAttribute('hidden', !isActive);
      }

      // After visibility changes, ask any sliders in the active panel to re-measure
      const activePanel = Array.from(this.tabPanels).find(p => p.dataset.itemId === itemId);
      if (activePanel instanceof HTMLElement) {
        const sliders = activePanel.querySelectorAll('swiper-slider');
        sliders.forEach((slider) => {
          slider.dispatchEvent(new CustomEvent('reinitialize-swiper', { bubbles: false }));
        });
      }

      // Update the "View All" link
      if (activeTab && this.viewAllLink) {
        const url = activeTab.dataset.url ?? '';
        if (typeof url === 'string' && url.trim() !== '') {
          this.viewAllLink.href = url;
        }
      }

      // Optionally focus the active tab
      if (focus && activeTab instanceof HTMLElement) activeTab.focus();
    }
  }

  customElements.define('td-tabbed-grouping', TdTabbedGrouping);
}
